import { useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import { DataGridPremium, GridColDef, GridDensity } from '@mui/x-data-grid-premium';
import { dataGridBoldClass, dataGridCellNoFocusOutlineClass } from '@shared/styles';
import { NotificationOption, Supplier } from '@retail/suppliers/types';
import clsx from 'clsx';
import { SuppliersTableActions } from './SuppliersTableActions';
import { StackedTextCell } from '@retail/components';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { PriceChangeNotificationOptionsMenu } from './PriceChangeNotificationOptionsMenu';
import { useRowColorGrouping } from '@retail/hooks';
import { Stack } from '@mui/system';
import { CheckIcon, RedCrossIcon } from '@shared/custom-icons';
import { ReadMore } from '@mui/icons-material';
import { useAppTFunction } from '@retail/app/i18n';

export interface SupplierRow extends Supplier {
  onEditPriceChangeConfig: ((priceChangeConfig: NotificationOption) => void) | undefined;
}

type Props = {
  suppliers: SupplierRow[];
  gridDensity: GridDensity;
  language: string;
  onSupplierSelect: (supplier: SupplierRow) => void;
  onShowUploadDialog?: (supplier: SupplierRow) => void;
  onUpdateNonStockSupplier?: (supplier: Supplier) => void;
  enablePriceChangeConfig: boolean;
  isLoading: boolean;
  countryCode: 'NO' | 'SE';
};

type GridColSupplier = GridColDef<SupplierRow>;

const baseColumnProps: Partial<GridColSupplier> = {
  width: 170,
  align: 'left',
  headerAlign: 'left',
  headerName: '',
};

export const SuppliersTable = ({
  language,
  gridDensity,
  suppliers,
  onSupplierSelect,
  onShowUploadDialog,
  onUpdateNonStockSupplier,
  enablePriceChangeConfig,
  isLoading,
  countryCode,
}: Props) => {
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columnConfigs: { show: boolean; column: GridColSupplier }[] = useMemo(() => {
    const actionsCount = [onUpdateNonStockSupplier, onShowUploadDialog].filter(
      (val) => !!val
    ).length;
    return [
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'name',
          cellClassName: () => dataGridBoldClass,
          headerName: t('suppliers.columns.participantAccountName'),
          headerAlign: 'left',
          align: 'left',
          width: 265,
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          sortable: false,
          field: 'supplierId',
          headerAlign: 'left',
          align: 'left',
          width: 120,
          renderHeader: () => (
            <StackedTextCell
              topLine={t('suppliers.columns.mgSupplierId')}
              bottomLine={
                countryCode === 'SE'
                  ? t('suppliers.columns.finfoSupplierId')
                  : t('suppliers.columns.nobbSupplierId')
              }
            />
          ),
          renderCell: ({ row }) => (
            <StackedTextCell topLine={row.mgSupplierId} bottomLine={row.finfoSupplierId} />
          ),
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 130,
          headerName: t('suppliers.columns.gln'),
          field: 'gln',
          headerAlign: 'left',
          align: 'left',
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 110,
          headerName: t('suppliers.columns.orgNumber'),
          field: 'orgNumber',
          headerAlign: 'left',
          align: 'left',
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'purchaseAgreementCount',
          align: 'center',
          headerAlign: 'center',
          headerName: t('suppliers.columns.purchaseAgreementCount'),
          width: 220,
          renderCell: ({ row }) => (
            <StackedTextCell
              centerAlign
              gap="small"
              topLine={
                <Stack flexDirection="row" alignItems="center" gap={0.5}>
                  <Typography>{row.purchaseAgreementCount}</Typography>
                  <Tooltip title={t('purchaseAgreements.supplier.viewAgreements')}>
                    <IconButton onClick={() => onSupplierSelect(row)}>
                      <ReadMore fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
              bottomLine={
                <Stack flexDirection="row" alignItems="center" gap={0.2}>
                  {t('suppliers.columns.hasActiveAgreement')}:
                  {row.hasSelectedSupplierAgreement ? (
                    <CheckIcon scale={0.8} />
                  ) : (
                    <RedCrossIcon scale={0.8} />
                  )}
                </Stack>
              }
            />
          ),
        },
      },

      {
        show: enablePriceChangeConfig,
        column: {
          ...baseColumnProps,
          field: 'priceChangeConfig',
          width: 140,
          align: 'center',
          headerAlign: 'center',
          headerName: t('suppliers.columns.priceChangeConfig.title'),
          renderCell: ({ row }) =>
            row.onEditPriceChangeConfig ? (
              <PriceChangeNotificationOptionsMenu
                priceChangeConfig={row.config?.priceChangeNotificationConfig || 'ignore'}
                onEditPriceChangeConfig={row.onEditPriceChangeConfig}
              />
            ) : null,
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'nonStockItemSupplier',
          align: 'center',
          headerAlign: 'center',
          headerName: t('suppliers.nonStockItemSupplier.title'),
          renderCell: ({ row }) =>
            row.nonStockItemSupplier ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t('suppliers.nonStockItemSupplier.added')}
              />
            ) : (
              '-'
            ),
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 110,
          headerName: t('export.vilmaLevel'),
          field: 'vilmaLevel',
          headerAlign: 'left',
          align: 'left',
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 180,
          headerName: t('suppliers.columns.adress'),
          field: 'location',
          headerAlign: 'left',
          align: 'left',
          renderCell: ({ row }) => (
            <StackedTextCell topLine={row.address} bottomLine={`${row.postalCode}, ${row.city}`} />
          ),
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 180,
          headerName: t('suppliers.columns.contact'),
          field: 'contact',
          headerAlign: 'left',
          align: 'left',
          renderCell: ({ row }) => (
            <StackedTextCell
              slotProps={{ bottomLine: { sx: { wordBreak: 'break-all' } } }}
              topLine={row.phoneNumber}
              bottomLine={row.email}
            />
          ),
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 110,
          headerName: t('assortment.admin.type'),
          field: 'type',
          headerAlign: 'left',
          align: 'left',
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          width: 110,
          headerName: t('suppliers.columns.purchaser'),
          field: 'purchaser',
          headerAlign: 'left',
          align: 'left',
        },
      },
      {
        show: actionsCount > 0,
        column: {
          field: 'actions',
          headerName: '',
          width: actionsCount * 40 + (actionsCount - 1) * 4 + 20,
          sortable: false,
          disableColumnMenu: true,
          renderCell: ({ row }) => (
            <SuppliersTableActions
              isNonStockSupplier={row.nonStockItemSupplier}
              onUpdateNonStockSupplier={
                onUpdateNonStockSupplier ? () => onUpdateNonStockSupplier(row) : undefined
              }
              onUploadAgreementsClick={
                onShowUploadDialog ? () => onShowUploadDialog(row) : undefined
              }
            />
          ),
        },
      },
    ];
  }, [enablePriceChangeConfig, onShowUploadDialog, onSupplierSelect, onUpdateNonStockSupplier, t]);

  const columns = useMemo(
    () => columnConfigs.filter(({ show }) => show).map(({ column }) => column),
    [columnConfigs]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<SupplierRow>
          getRowId={(row) => row.mgSupplierId}
          columns={columns}
          rows={suppliers}
          getRowClassName={getRowColorForStripedTable}
          rowHeight={65}
          density={gridDensity}
          getCellClassName={() => clsx(dataGridCellNoFocusOutlineClass)}
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          loading={isLoading}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
        />
      </Box>
    </Box>
  );
};
